<script setup lang="ts">
import {
  ButtonComponent,
  Card,
  defaultFormatter,
  FlexComponent,
  formatNumberValue,
  formatterProviderKey,
  InputNumberComponent,
  NoteComponent,
  TrafficSourceCodeEnum,
  TypographyComponent,
} from '@metapic-apps/shared'
import { ElForm, ElFormItem, ElNotification, type FormInstance, type FormItemRule } from 'element-plus'
import { type Component, computed, inject, reactive, ref } from 'vue'

import { useUpdateCpms } from '@/api/advertiser.api'
import InstagramIcon from '@/assets/icons/instagram.svg?component'
import SnapchatIcon from '@/assets/icons/snapchat-costs-icon.svg?component'
import TiktokIcon from '@/assets/icons/tiktok.svg?component'
import type { AdvertiserType } from '@/lib/types/advertiser.types'

type TrafficSource = {
  code: TrafficSourceCodeEnum
  label: string
  icon: Component | null
}

const TRAFFIC_SOURCES: TrafficSource[] = [
  { code: TrafficSourceCodeEnum.Instagram, label: 'Instagram', icon: InstagramIcon },
  { code: TrafficSourceCodeEnum.Tiktok, label: 'TikTok', icon: TiktokIcon },
  { code: TrafficSourceCodeEnum.Snapchat, label: 'Snapchat', icon: SnapchatIcon },
  { code: TrafficSourceCodeEnum.Other, label: 'Other', icon: null },
]

const STEP = 1

const props = defineProps<{
  advertiserId: string
  cpms: AdvertiserType['cpms']
  currencyCode: string
}>()

const advertiserId = computed(() => props.advertiserId)
const { formatMoney } = inject(formatterProviderKey, defaultFormatter)

const { loading, error, updateCpms } = useUpdateCpms(advertiserId)

const formRef = ref<FormInstance>()

const cpmRules: FormItemRule[] = [
  {
    validator: (_rule, value, callback) => {
      const stringValue = String(value ?? '').trim()
      if (stringValue !== '' && (Number.isNaN(Number(stringValue)) || Number(stringValue) < 0)) {
        return callback(new Error('CPM must be 0 or greater'))
      }
      return callback()
    },
    trigger: 'change',
  },
]

const form = reactive<Record<TrafficSourceCodeEnum, number | undefined>>({
  [TrafficSourceCodeEnum.Instagram]: undefined,
  [TrafficSourceCodeEnum.Tiktok]: undefined,
  [TrafficSourceCodeEnum.Snapchat]: undefined,
  [TrafficSourceCodeEnum.Other]: undefined,
  ...props.cpms.values,
})

const defaultCpms = computed(() => props.cpms.defaults)

const originalCpms = computed(() => props.cpms.values)

const getPlaceholder = (code: TrafficSourceCodeEnum): string => {
  const val = defaultCpms.value[code]
  return val !== undefined ? formatNumberValue(val, 'standard', 'decimal', 2, 'en-GB', 2) : ''
}

const getDefaultLabel = (code: TrafficSourceCodeEnum): string => {
  const val = defaultCpms.value[code]
  if (val === undefined || !props.currencyCode) {
    return ''
  }
  const formattedCPM = formatMoney({ amount: val, currency: props.currencyCode }, { maximumFractionDigits: 2, allowZero: true })
  return `Default: ${formattedCPM}`
}

const save = async () => {
  const valid = await formRef.value?.validate().catch(() => false)
  if (!valid) {
    return
  }
  await updateCpms(form, originalCpms.value)
  if (!error.value) {
    ElNotification.success('CPM values saved successfully')
  }
}
</script>

<template>
  <Card title="CPM value" title-size="small" class="cpm-settings">
    <NoteComponent variant="body-medium">
      Your CPM (Cost per Mille/Cost per Thousand) determines how your content's media value is calculated. If left empty, we'll apply the
      standard market CPM automatically.
    </NoteComponent>

    <ElForm ref="formRef" :model="form">
      <FlexComponent direction="row" :gap="8" class="cpm-settings__sources">
        <div v-for="source in TRAFFIC_SOURCES" :key="source.code" class="cpm-settings__source-card">
          <FlexComponent direction="row" align="center" :gap="8" class="cpm-settings__source-heading">
            <component :is="source.icon" v-if="source.icon" class="cpm-settings__source-icon" />
            <TypographyComponent variant="title-large">{{ source.label }}</TypographyComponent>
          </FlexComponent>

          <ElFormItem :prop="source.code" :rules="cpmRules">
            <InputNumberComponent
              :model-value="form[source.code] ?? null"
              controls
              :min="0"
              :step="STEP"
              :precision="2"
              :placeholder="getPlaceholder(source.code)"
              class="cpm-settings__stepper-value"
              @update:model-value="form[source.code] = $event ?? undefined"
            />
          </ElFormItem>

          <TypographyComponent v-if="getDefaultLabel(source.code)" variant="body-medium">
            {{ getDefaultLabel(source.code) }}
          </TypographyComponent>
        </div>
      </FlexComponent>
    </ElForm>

    <template #footer>
      <ButtonComponent variant="accent" :is-loading="loading" class="cpm-settings__save-btn" @click="save"> Save </ButtonComponent>
    </template>
  </Card>
</template>

<style scoped lang="scss">
.cpm-settings {
  &__stepper-value {
    width: 150px;
  }

  &__sources {
    flex-wrap: wrap;
  }

  &__source-card {
    flex: 1;
    min-width: 120px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 16px;
    border: 1px solid var(--black-8);
    border-radius: 16px;
  }

  &__source-heading {
    justify-content: center;
  }

  &__source-icon {
    width: 24px;
    height: 24px;
  }

  &__save-btn {
    align-self: flex-start;
  }
}
</style>
